/* eslint-disable no-shadow */
/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import PlayerStandingsCardV3 from '../../../../../components/cards/PlayerStandingsCard/PlayerStandingsCardV3';
import StandingsHeaderColumns from './StandingsHeaderColumns';
import InfoButton from '../../../../../components/InfoButton';
import TeamStandingsCardV3 from '../../../../../components/cards/TeamStandingsCard/TeamStandingsCardV3';

import { getDrawStats } from '../../../../../actions/event-profile-actions';
import { sportTypeIds } from '../../../../../utils/eventConstants';
import { hasRetiredTag } from '../../../../../utils/types';
import { rankList } from '../../../../../utils/events';
import { memberDetailToStandingsPlayer, hasParticipatingMembers } from '../../../../../utils/standingsPlayerMapper';
import { drawTagTypeShape } from '../../../../../propTypes';

const StandingsV3 = (props) => {
    const {
        drawStandings,
        drawStandingsLoading,
        sportTypeId,
        isPttEvent,
        drawTagTypes,
    } = props;

    const { event, currentDraw } = useSelector(({ eventProfile }) => eventProfile);
    const dispatch = useDispatch();

    const standingsData = drawStandings && (drawStandings.playerStats || drawStandings.teamStats);
    const isPickleballEvent = sportTypeId === sportTypeIds.PICKLEBALL;

    const refresh = () => {
        dispatch(getDrawStats(event.data.id, currentDraw.data.id));
    };

    const rankOrderDeterminationSequence = () => {
        const headToHead = 'Head to Head result if only two players are tied';
        const totalMatchPlayed = 'Greatest Number of Matches Played';
        const totalWins = 'Greatest Number of Wins';

        const isTennisEvent = isPttEvent ? [
            totalWins,
            totalMatchPlayed,
            `Head-to-Head (if only 2 players are tied)`,
            `If 3+ players tied:`,
            [
                `Players with fewer Matches Played are eliminated`,
                `Head-to-head when 2 players remain`,
                `If more than 1 player still remains`,
                [
                    `% Sets Won`,
                    `% Games Won`,
                    `UTR`,
                    `Coin Toss (random)`,
                ],
            ]] : [
            totalWins,
            totalMatchPlayed,
            headToHead,
            `% of sets won`,
            `% of games won`,
            'Overall Current Rating',
        ];
        return isPickleballEvent ? [
            'Greatest number of matches won',
            'Head-to-head matches won among the tied players/teams',
            'Point differential of all games played',
            'Head-to-head point differential',
            'Point differential against the next-highest player/team',
        ] : isTennisEvent;
    };

    const shouldShowForfeitText = useMemo(() => {
        if (drawStandings?.playerStats || drawStandings?.teamStats) {
            return standingsData
                .filter(hasParticipatingMembers)
                .filter(standing => (hasRetiredTag(standing?.tagId) && standing?.stats !== null) || !!standing.outcome)?.length > 0;
        }

        return false;
    }, [standingsData, drawStandings]);

    useEffect(() => {
        if (!drawStandings && !drawStandingsLoading) {
            refresh();
        }
    }, [currentDraw.data?.id]);

    if (drawStandingsLoading) {
        return <div data-testid="standings-v3.loading-msg">Loading Standings...</div>;
    }
    if (!standingsData) {
        return (
            <div className="d-flex flex-row jcsb aic">
                <div data-testid="standings-v3.no-stats-msg">No Stats For Draw</div>
                <div className="text-right">
                    <div className="ml16 btn btn-sm btn-default" onClick={refresh} data-testid="standings-v3.refresh-btn-no-data">Refresh</div>
                </div>
            </div>
        );
    }

    return (
        <div className={``}>
            <div className="text-right" data-testid="standings-v3.rank-order-header">How is rank order determined?
                <InfoButton
                    alignRight={true}
                    hoverText={(
                        <>
                            Rank order for standings is determined by
                            {rankList(rankOrderDeterminationSequence())}
                            {isPickleballEvent && ('Note: Players/teams that have withdrawn from the bracket are not eligible to be considered when going to a tie-breaker and shall only have their record of matches won considered.')}
                        </>
                    )}
                />
                <div className="ml16 btn btn-sm btn-default" onClick={refresh} data-testid="standings-v3.refresh-btn">Refresh</div>
            </div>
            <StandingsHeaderColumns
                sportTypeId={sportTypeId}
                isIndexDisplayed={!!standingsData}
            />
            {drawStandings.playerStats && standingsData
                .filter(hasParticipatingMembers)
                .map((standing, index) => {
                    return (
                        <PlayerStandingsCardV3
                            key={standing.id}
                            drawMedal={standing.drawMedal}
                            divisionMedal={standing.divisionMedal}
                            divisionName={standing.divisionName}
                            player={memberDetailToStandingsPlayer(standing.participatingMembers[0])}
                            stats={standing.stats}
                            sportTypeId={sportTypeId}
                            index={index + 1}
                            isIndexDisplayed={!!standingsData}
                            standingsPosition={standing.standingsPosition}
                            outcome={standing?.outcome}
                            tagId={standing?.tagId}
                            isPttEvent={isPttEvent}
                            drawTagTypes={drawTagTypes}
                        />
                    );
                })}
            {drawStandings.teamStats && standingsData
                .filter(hasParticipatingMembers)
                .map((standing) => {
                    const teamPlayers = (standing.participatingMembers ?? []).map(memberDetailToStandingsPlayer);

                    return (
                        <TeamStandingsCardV3
                            key={standing.teamId}
                            teamPlayers={teamPlayers}
                            drawMedal={standing.drawMedal}
                            divisionMedal={standing.divisionMedal}
                            stats={standing.stats}
                            sportTypeId={sportTypeId}
                            isIndexDisplayed={!!standingsData}
                            standingsPosition={standing.standingsPosition}
                            outcome={standing?.outcome}
                            tagId={standing?.tagId}
                            isPttEvent={isPttEvent}
                            drawTagTypes={drawTagTypes}
                        />
                    );
                })}
            {shouldShowForfeitText && (
                <div className="mt16 pl24 pr24 text-center text-small text-black" data-testid="standings-v3.forfeit-text">
                    Withdrawn, retired or forfeited players will be excluded from the standings calculation, but match results will still count for their opponents who remain in the event.
                </div>
            )}
        </div>
    );
};

StandingsV3.propTypes = {
    drawStandings: PropTypes.shape({
        playerStats: PropTypes.arrayOf(PropTypes.shape({
            memberId: PropTypes.number,
            position: PropTypes.number,
            wins: PropTypes.number,
            losses: PropTypes.number,
        })),
        teamStats: PropTypes.arrayOf(PropTypes.shape({
            teamId: PropTypes.number,
            memberIds: PropTypes.arrayOf(PropTypes.number),
            wins: PropTypes.number,
            losses: PropTypes.number,
        })),
    }),
    drawStandingsLoading: PropTypes.bool,
    sportTypeId: PropTypes.number,
    isPttEvent: PropTypes.bool,
    drawTagTypes: PropTypes.arrayOf(drawTagTypeShape).isRequired,
};

StandingsV3.defaultProps = {
    drawStandings: null,
    drawStandingsLoading: false,
    sportTypeId: sportTypeIds.TENNIS,
    isPttEvent: false,
};

export default StandingsV3;
